"use client"

import React, { useState, useEffect } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Phone, XCircle, Archive, Filter, AlertCircle, ArrowLeft, Bot, PhoneIncoming } from "lucide-react"
import { useCalls } from "@/hooks/use-calls"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { CALLS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { cn } from "@/lib/utils"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Pagination } from "@/components/ui/pagination"
import { TooltipProvider } from "@/components/ui/tooltip"
import { CallsStats } from "./calls-stats"
import { CallsFilters } from "./calls-filters"
import { CallRow } from "./call-row"
import { TranscriptDialog } from "./transcript-dialog"
import { DeleteCallDialog } from "./delete-call-dialog"
import { ToolCallsDialog } from "./tool-calls-dialog"
import { CallFeedbackDialog } from "./call-feedback-dialog"
import { RawCallDataDialog } from "./raw-call-data-dialog"
import { QAFeedbackLinkButton } from "@/components/ai/feedback/qa-feedback-link-button"
import type { TranscriptMessage } from "@/types/calls"
import type { CallSource, UnifiedCall } from "@/types/unified-call"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type FeedbackStatus = "pending" | "in_progress" | "resolved";

interface TranscriptItem {
  role: "agent" | "user"
  message: string
}

export function CallsPage() {

  const tUi = useTranslations("Calls.CallsCallsPage")

  const router = useRouter()
  const searchParams = useSearchParams()
  const agentIdParam = searchParams.get("agentId")
  const initialAgentId = agentIdParam || null
  const callIdParam = searchParams.get("callId")
  const initialCallId = callIdParam || null

  const {
    calls,
    isLoading,
    isInitialLoading,
    callsPage,
    callsPageSize,
    callsTotal,
    archivedCount,
    showArchived,
    filters,
    stats,
    hasActiveFilters,
    goToCallsPage,
    changeCallsPageSize,
    refreshCalls,
    toggleArchived,
    updateSearch,
    updateStatus,
    updateAgentId,
    updateSource,
    clearFilters,
  } = useCalls(initialAgentId, initialCallId)

  const { user, fetchWithAuth } = useAuth()
  const { can } = usePermissions()

  // Transcript dialog state
  const [isTranscriptDialogOpen, setIsTranscriptDialogOpen] = useState(false)
  const [selectedTranscript, setSelectedTranscript] = useState<TranscriptItem[]>([])
  const [transcriptLoading, setTranscriptLoading] = useState(false)
  const [selectedCallId, setSelectedCallId] = useState<string>("")
  const [rawCallId, setRawCallId] = useState<string | null>(null)
  const rawCall = calls.find(call => call.id === rawCallId && call.type === "voice_agent")

  // Delete dialog state
  const [deleteCallId, setDeleteCallId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Tool calls dialog state
  const [isToolCallsDialogOpen, setIsToolCallsDialogOpen] = useState(false)
  const [selectedCallForTools, setSelectedCallForTools] = useState<TranscriptMessage[]>([])

  // Feedback dialog state
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [selectedCallForFeedback, setSelectedCallForFeedback] = useState<UnifiedCall | null>(null)
  const [feedbackStatuses, setFeedbackStatuses] = useState<Record<string, FeedbackStatus>>({})

  const isSuperUser = user?.role === "super_user"
  // Permission-gated (matches the calls.delete-gated DELETE route) so any
  // admin-level role — including integrator — gets the deletion UI.
  const canDeleteCalls = can(CALLS_PERMISSIONS.DELETE)
  const tableMinWidthClass = isSuperUser ? "min-w-[1700px]" : "min-w-[1300px]"

  // Load feedback statuses for voice agent calls
  useEffect(() => {
    const loadFeedbackStatuses = async () => {
      // Get conversation IDs from voice agent calls
      const conversationIds = calls
        .filter((c) => c.type === "voice_agent" && c.voiceAgentData?.conversationId)
        .map((c) => c.voiceAgentData!.conversationId)

      if (conversationIds.length === 0) {
        setFeedbackStatuses({})
        return
      }

      try {
        // Fetch feedback for all conversation IDs
        const res = await fetchWithAuth(
          `/api/ai/feedback?agent_type=voice_agent&limit=100`
        )
        const data = await res.json()

        if (data.items) {
          const statuses: Record<string, FeedbackStatus> = {}
          data.items.forEach((item: { reference_id: string; status: FeedbackStatus }) => {
            if (conversationIds.includes(item.reference_id)) {
              statuses[item.reference_id] = item.status
            }
          })
          setFeedbackStatuses(statuses)
        }
      } catch (error) {
        console.error("Failed to load feedback statuses:", error)
      }
    }

    loadFeedbackStatuses()
  }, [calls, fetchWithAuth])

  // Open feedback dialog handler
  const handleOpenFeedback = (call: UnifiedCall) => {
    setSelectedCallForFeedback(call)
    setIsFeedbackDialogOpen(true)
  }

  // Feedback changed handler - refresh statuses
  const handleFeedbackChanged = () => {
    // Trigger a refresh of feedback statuses
    refreshCalls()
  }

  // Get filtered agent name for display
  const filteredAgentName = filters.agentId
    ? calls.find((c) => c.type === "voice_agent" && c.voiceAgent?.id === filters.agentId)?.voiceAgent?.name
    : null

  // Clear agent filter handler
  const clearAgentFilter = () => {
    updateAgentId(null)
    router.push("?page=call-logs")
  }

  // View transcript handler
  const handleViewTranscript = async (callId: string) => {
    setTranscriptLoading(true)
    setSelectedCallId(callId)

    // Check if this is a unified call in the current list
    const call = calls.find(c => c.id === callId)

    if (call) {
      // For PBX calls with linked AI transcript, use the linked transcript directly
      if (call.type === "pbx" && call.pbxData?.linkedAiCall) {
        const transcript = call.pbxData.linkedAiCall.transcript || []
        setSelectedTranscript(
          transcript.map((m) => ({
            role: m.role as "agent" | "user",
            message: m.message || "",
          }))
        )
        setIsTranscriptDialogOpen(true)
        setTranscriptLoading(false)
        return
      }

      // For voice agent calls, use the transcript directly if available
      if (call.type === "voice_agent" && call.voiceAgentData?.transcript) {
        const transcript = call.voiceAgentData.transcript
        setSelectedTranscript(
          transcript.map((m) => ({
            role: m.role as "agent" | "user",
            message: m.message || "",
          }))
        )
        setIsTranscriptDialogOpen(true)
        setTranscriptLoading(false)
        return
      }
    }

    // Fallback: fetch from API for voice agent calls
    // Extract the original ID from unified ID (va_123 -> 123)
    const originalId = callId.startsWith("va_") ? callId.slice(3) : callId

    try {
      const response = await fetchWithAuth(`/api/calls/${originalId}/transcript`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSelectedTranscript(data.transcript || [])
        setIsTranscriptDialogOpen(true)
      } else if (response.status === 403) {
        console.error("Access denied to transcript")
        setSelectedTranscript([])
        setIsTranscriptDialogOpen(true)
      } else {
        console.error("Failed to fetch call transcript")
      }
    } catch (error) {
      console.error("Error fetching transcript:", error)
    } finally {
      setTranscriptLoading(false)
    }
  }

  // Delete call handler
  const handleDeleteCall = async () => {
    if (!deleteCallId) return

    // Extract original ID from unified ID
    const originalId = deleteCallId.startsWith("va_") ? deleteCallId.slice(3) : deleteCallId

    setIsDeleting(true)
    try {
      const response = await fetchWithAuth(`/api/calls/${originalId}`, {
        method: "DELETE",
      })

      if (response.ok) {
        refreshCalls()
      } else {
        console.error("Failed to delete call")
      }
    } catch (error) {
      console.error("Error deleting call:", error)
    } finally {
      setIsDeleting(false)
      setDeleteCallId(null)
    }
  }

  // Handle entering archive mode
  const handleEnterArchive = () => {
    // Clear URL params if agent filter was active
    if (filters.agentId !== null) {
      router.push("?page=call-logs")
    }
    // toggleArchived resets all filters automatically
    toggleArchived(true)
  }

  // Handle viewing tool calls
  const handleViewToolCalls = (transcript: TranscriptMessage[]) => {
    setSelectedCallForTools(transcript)
    setIsToolCallsDialogOpen(true)
  }

  // Handle source filter change
  const handleSourceChange = (source: string) => {
    updateSource(source as CallSource)
  }

  // Check permission to view calls
  if (!can(CALLS_PERMISSIONS.VIEW)) {
    return (
      <div className="p-6 space-y-6 max-w-[1800px] mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription><span key={tUi("youDonTHavePermissionToViewCalls")}>{tUi("youDonTHavePermissionToViewCalls")}</span></AlertDescription>
        </Alert>
        <Button onClick={() => router.push("?page=voice-agents")} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span><span key={tUi("backToAgents")}>{tUi("backToAgents")}</span></span>
        </Button>
      </div>
    )
  }

  if (isInitialLoading) {
    return (
      <div className="p-6 space-y-6 max-w-[1800px] mx-auto">
        <div className="text-center py-12">
          <div className="text-gray-500"><span key={tUi("loadingCalls")}>{tUi("loadingCalls")}</span></div>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
    <div className="p-6 space-y-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 key={showArchived ? tUi("archivedCalls") : tUi("callRecords")} className="text-2xl font-semibold text-foreground">
            {showArchived ? tUi("archivedCalls") : tUi("callRecords")}
          </h1>
          <p key={showArchived
              ? tUi("callsFromDeletedVoiceAgents")
              : tUi("viewAndManageCallTranscriptionsAndAnalytics")} className="text-gray-600 mt-1">
            {showArchived
              ? tUi("callsFromDeletedVoiceAgents")
              : tUi("viewAndManageCallTranscriptionsAndAnalytics")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {archivedCount > 0 && !showArchived && (
            <Button variant="outline" onClick={handleEnterArchive}>
              <Archive className="w-4 h-4 mr-2" />
              <span><span key={tUi("archive")}>{tUi("archive")}</span></span>{archivedCount}<span>)</span>
            </Button>
          )}
        </div>
      </div>

      {/* Source Tabs */}
      {!showArchived && (
        <Tabs value={filters.source} onValueChange={handleSourceChange} className="w-full sm:w-fit">
          <TabsList>
              <TabsTrigger value="all" className="gap-2">
                <Phone className="w-4 h-4" />
                <span><span key={tUi("allCalls")}>{tUi("allCalls")}</span></span>
                {stats.totalCalls > 0 && (
                  <span className="text-xs bg-gray-200 px-1.5 rounded">{stats.totalCalls}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="voice_agent" className="gap-2">
                <Bot className="w-4 h-4" />
                <span><span key={tUi("voiceAgent")}>{tUi("voiceAgent")}</span></span>
                {stats.voiceAgentCalls > 0 && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 rounded">{stats.voiceAgentCalls}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="pbx" className="gap-2">
                <PhoneIncoming className="w-4 h-4" />
                <span><span key={"PBX"}>{"PBX"}</span></span>
                {stats.pbxCalls > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 rounded">{stats.pbxCalls}</span>
                )}
              </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Archive mode indicator */}
      {showArchived && (
        <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <Archive className="w-4 h-4 text-orange-600" />
          <span className="text-sm text-orange-800"><TranslationText>{tUi.rich("viewingTag0Tag1ArchivedCallsTag1Tag0From", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>) })}</TranslationText></span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-orange-600 hover:text-orange-800 hover:bg-orange-100"
            onClick={() => toggleArchived(false)}
          >
            <XCircle className="w-4 h-4 mr-1" />
            <span><span key={tUi("exitArchive")}>{tUi("exitArchive")}</span></span>
          </Button>
        </div>
      )}

      {/* Agent filter indicator */}
      {filters.agentId !== null && !showArchived && (
        <div className="flex flex-col gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg sm:flex-row sm:items-center sm:gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Filter className="w-4 h-4 shrink-0 text-blue-600" />
            <span className="min-w-0 text-sm text-blue-800"><TranslationText>{tUi.rich("filteredByAgentTag0Value0Tag0", { value0: String(filteredAgentName || `ID ${filters.agentId}`), tag0: chunks => (<strong className="block truncate sm:inline sm:whitespace-normal" title={filteredAgentName || `ID ${filters.agentId}`}>{chunks}</strong>) })}</TranslationText></span>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:shrink-0 sm:flex-nowrap">
            <QAFeedbackLinkButton
              agentId={filters.agentId}
              agentType="voice_agent"
              agentName={filteredAgentName || undefined}
              className="flex-1 justify-center whitespace-nowrap px-2 sm:flex-none sm:px-4"
            />
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 whitespace-nowrap text-blue-600 hover:text-blue-800 hover:bg-blue-100 sm:flex-none"
              onClick={clearAgentFilter}
            >
              <XCircle className="w-4 h-4 mr-1" />
              <span><span key={tUi("clearFilter")}>{tUi("clearFilter")}</span></span>
            </Button>
          </div>
        </div>
      )}

      {/* Summary Cards - show stats from server */}
      <CallsStats stats={stats} showCost={isSuperUser} />

      {/* Search and Filters */}
      <CallsFilters
        filters={filters}
        totalCount={callsTotal}
        showArchived={showArchived}
        onSearchChange={updateSearch}
        onStatusChange={updateStatus}
        onClearFilters={clearFilters}
      />

      {/* Calls Table */}
      {calls.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-lg border border-gray-200">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-gray-400" />
          </div>
          <h3 key={hasActiveFilters ? tUi("noCallsFound") : tUi("noCallsRecordedYet")} className="text-lg font-medium text-gray-900 mb-2">
            {hasActiveFilters ? tUi("noCallsFound") : tUi("noCallsRecordedYet")}
          </h3>
          <p key={hasActiveFilters
              ? tUi("tryAdjustingYourSearchTermsOrFiltersTo")
              : tUi("callRecordsWillAppearHereOnceYourAgents")} className="text-gray-600 mb-6">
            {hasActiveFilters
              ? tUi("tryAdjustingYourSearchTermsOrFiltersTo")
              : tUi("callRecordsWillAppearHereOnceYourAgents")}
          </p>
        </div>
      ) : (
        <div
          aria-busy={isLoading}
          className={cn(
            "flex flex-col [--table-card-margin-inline:0px] transition-opacity sm:bg-white sm:rounded-lg sm:border sm:border-gray-200 sm:h-[calc(100vh-420px)] sm:min-h-[400px] sm:overflow-hidden",
            isLoading && "opacity-60",
          )}
        >
          {/* Scrollable table area */}
            <Table className={tableMinWidthClass} containerClassName="flex-1 min-h-0 overflow-auto [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              <TableHeader className="sticky top-0 z-10">
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[100px]"><span key={tUi("callID")}>{tUi("callID")}</span></TableHead>
                  <TableHead className="w-[120px]"><span key={tUi("type")}>{tUi("type")}</span></TableHead>
                  {isSuperUser && <TableHead><span key={tUi("service")}>{tUi("service")}</span></TableHead>}
                  {isSuperUser && <TableHead><span key={tUi("direction")}>{tUi("direction")}</span></TableHead>}
                  <TableHead><span key={tUi("agentFlow")}>{tUi("agentFlow")}</span></TableHead>
                  <TableHead><span key={tUi("fromNumber")}>{tUi("fromNumber")}</span></TableHead>
                  <TableHead><span key={tUi("toNumber")}>{tUi("toNumber")}</span></TableHead>
                  <TableHead><span key={tUi("status")}>{tUi("status")}</span></TableHead>
                  <TableHead><span key={tUi("duration")}>{tUi("duration")}</span></TableHead>
                  {isSuperUser && <TableHead><span key={tUi("agentCost")}>{tUi("agentCost")}</span></TableHead>}
                  <TableHead><span key={tUi("date")}>{tUi("date")}</span></TableHead>
                  <TableHead><span key={tUi("transcript")}>{tUi("transcript")}</span></TableHead>
                  <TableHead className="w-[120px]"><span key={tUi("actions")}>{tUi("actions")}</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((call) => (
                  <CallRow
                    key={call.id}
                    call={call}
                    isSuperUser={isSuperUser}
                    canDelete={!!canDeleteCalls}
                    onViewTranscript={handleViewTranscript}
                    onViewDetails={setRawCallId}
                    onViewToolCalls={handleViewToolCalls}
                    onDelete={setDeleteCallId}
                    feedbackStatus={
                      call.type === "voice_agent" && call.voiceAgentData?.conversationId
                        ? feedbackStatuses[call.voiceAgentData.conversationId] || null
                        : null
                    }
                    onAddFeedback={handleOpenFeedback}
                  />
                ))}
              </TableBody>
            </Table>

          {/* Pagination - always visible at bottom */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-white">
            <Pagination
              page={callsPage}
              pageSize={callsPageSize}
              total={callsTotal}
              onPageChange={goToCallsPage}
              onPageSizeChange={changeCallsPageSize}
              disabled={isLoading || callsTotal === 0}
            />
          </div>
        </div>
      )}

      {/* Raw data dialog */}
      {isSuperUser && rawCall && (
        <RawCallDataDialog
          open
          onOpenChange={(open) => { if (!open) setRawCallId(null) }}
          callId={rawCall.id}
          rawData={rawCall.voiceAgentData?.rawData}
        />
      )}

      {/* Transcript Dialog */}
      <TranscriptDialog
        open={isTranscriptDialogOpen}
        onOpenChange={setIsTranscriptDialogOpen}
        callId={selectedCallId}
        transcript={selectedTranscript}
        isLoading={transcriptLoading}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteCallDialog
        open={deleteCallId !== null}
        onOpenChange={(open) => !open && setDeleteCallId(null)}
        onConfirm={handleDeleteCall}
        isDeleting={isDeleting}
      />

      {/* Tool Calls Dialog */}
      <ToolCallsDialog
        open={isToolCallsDialogOpen}
        onOpenChange={setIsToolCallsDialogOpen}
        transcript={selectedCallForTools}
        isSuperUser={isSuperUser}
      />

      {/* Feedback Dialog */}
      <CallFeedbackDialog
        open={isFeedbackDialogOpen}
        onOpenChange={setIsFeedbackDialogOpen}
        call={selectedCallForFeedback}
        onFeedbackChanged={handleFeedbackChanged}
      />
    </div>
    </TooltipProvider>
  )
}
